import { useState } from 'react';
import { calc, cancelSuggest, fDate, inr, type PaymentType } from '@nandanam/domain';
import { useStore } from '../app/store';
import { Field, Modal, MoneyInput } from '../ui/components';
import { PAYTYPE } from '../app/selectors';
import { ErrorBox, Figs, ModeChips, RecordPick, Success, ViewOnlyNote, todayISO, useSubmit } from './kit';

const PAYABLE = ['upcoming', 'inprogress', 'closing'];

/** Record payment. Opened from a record, or globally (Today) with a booking picker. */
export function PaymentOverlay({ id: given }: { id?: string }) {
  const S = useStore();
  const [id, setId] = useState(given ?? '');
  const r = id ? S.rec(id) : undefined;
  const k = r ? calc(r) : null;
  const firstPay = r ? !r.payments.some(p => !p.voided && p.type !== 'refund') : false;
  const [type, setType] = useState<Exclude<PaymentType, 'refund'>>(firstPay ? 'advance' : 'part');
  const [amount, setAmount] = useState<number | ''>(k && k.balance > 0 ? k.balance : '');
  const [date, setDate] = useState(todayISO(S));
  const [ref, setRef] = useState('');
  const [mode, setMode] = useState(S.state.settings.payModes[0] ?? 'UPI');
  // Generated once, before the first attempt: a retry after a timeout reuses it and can never double-count.
  const [payId] = useState(() => S.newId());
  const [done, setDone] = useState<{ amount: number } | null>(null);
  const sub = useSubmit();

  const pick = (x: string) => { setId(x); const rr = S.rec(x); const kk = rr ? calc(rr) : null; setAmount(kk && kk.balance > 0 ? kk.balance : ''); setType(rr && !rr.payments.some(p => !p.voided && p.type !== 'refund') ? 'advance' : 'part'); };
  const over = k && amount !== '' && amount > k.balance;
  const problem = !r ? 'Choose a booking' : !PAYABLE.includes(S.phase(r)) ? 'Payments can be recorded on confirmed bookings only' : amount === '' || amount <= 0 ? 'Enter an amount' : over ? `Amount is more than the balance of ${inr(k!.balance)}` : null;

  const save = () => sub.run(async () => {
    const x = await S.act('record.payment', `record:${id}`, { id, payment: { id: payId, type: amount === k!.balance && type === 'part' ? 'final' : type, mode, amount, ref: ref.trim(), date } });
    if (x.ok) setDone({ amount: Number(amount) });
    return x;
  });

  if (done && r) {
    const kk = calc(r);
    return (
      <Modal kicker="Payment" title="Payment recorded" onClose={() => S.close()}>
        <Success title={`${inr(done.amount)} received`} body={`${S.cust(r.cust).name} · ${kk.balance > 0 ? `${inr(kk.balance)} still due` : 'paid in full'}`}
          actions={[{ label: 'Print bill', go: () => S.nav('bill', { id }) }, { label: 'WhatsApp “Payment received”', go: () => S.open('message', { id, tpl: type === 'advance' ? 'advance' : 'payment' }) }, { label: 'Done', primary: true, go: () => S.close() }]} />
      </Modal>
    );
  }
  return (
    <Modal kicker="Payment" title="Record payment" sub={r ? `${S.num(r)} · ${S.cust(r.cust).name}` : undefined} onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Cancel</button><button className="btn primary" disabled={!!problem || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Saving…' : 'Record payment'}</button></>}>
      <ViewOnlyNote />
      {!given && <RecordPick value={id} onChange={pick} filter={x => PAYABLE.includes(S.phase(x)) && calc(x).balance > 0} />}
      {r && k && <>
        <Figs items={[['Total', inr(k.total)], ['Received', inr(k.paid)], ['Balance after', inr(Math.max(0, k.balance - Number(amount || 0)))]]} />
        <div className="grid2">
          <Field label="Transaction"><select className="select" value={type} onChange={e => setType(e.target.value as typeof type)}>{(['advance', 'part', 'final'] as const).map(t => <option key={t} value={t}>{PAYTYPE[t]}</option>)}</select></Field>
          <Field label="Amount"><MoneyInput value={amount} onChange={setAmount} /></Field>
          <Field label="Date"><input type="date" className="input" value={date} max={todayISO(S)} onChange={e => setDate(e.target.value)} /></Field>
          <Field label="Reference (UPI / UTR)"><input className="input" placeholder="Optional" value={ref} onChange={e => setRef(e.target.value)} /></Field>
        </div>
        <Field label="Mode"><ModeChips value={mode} onChange={setMode} /></Field>
        {over && <div className="error-box">Amount is more than the balance of {inr(k.balance)}. If the guest paid extra, record the balance and refund the difference.</div>}
        <div className="muted" style={{ fontSize: 12 }}>Caution deposits are recorded separately (Deposit tab) — they are not booking payments.</div>
      </>}
      <ErrorBox error={sub.error} />
    </Modal>
  );
}

export function DepositOverlay({ id }: { id: string }) {
  const S = useStore(), r = S.rec(id);
  const [amount, setAmount] = useState<number | ''>(r?.deposit.required ?? '');
  const [date, setDate] = useState(todayISO(S));
  const [mode, setMode] = useState(S.state.settings.payModes.includes('Cash') ? 'Cash' : S.state.settings.payModes[0]!);
  const [done, setDone] = useState(false);
  const sub = useSubmit();
  if (!r) return null;
  const save = () => sub.run(async () => { const x = await S.act('record.collectDeposit', `record:${id}`, { id, deposit: { amount, mode, date } }); if (x.ok) setDone(true); return x; });
  if (done) return <Modal kicker="Caution deposit" title="Deposit recorded" onClose={() => S.close()}><Success title={`${inr(Number(amount))} held`} body="Refundable after inspection at check-out. Not part of booking revenue." actions={[{ label: 'WhatsApp “Deposit received”', go: () => S.open('message', { id, tpl: 'deposit' }) }, { label: 'Done', primary: true, go: () => S.close() }]} /></Modal>;
  return (
    <Modal kicker="Caution deposit" title="Collect deposit" sub={`${S.num(r)} · ${S.cust(r.cust).name}`} onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Cancel</button><button className="btn primary" disabled={!amount || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Saving…' : 'Record deposit'}</button></>}>
      <ViewOnlyNote />
      <div style={{ fontSize: 14 }}>Refundable caution deposit, held separately from booking revenue. Required for this booking: <b>{inr(r.deposit.required)}</b>.</div>
      <div className="grid2">
        <Field label="Amount collected"><MoneyInput value={amount} onChange={setAmount} /></Field>
        <Field label="Date"><input type="date" className="input" value={date} max={todayISO(S)} onChange={e => setDate(e.target.value)} /></Field>
      </div>
      <Field label="Mode"><ModeChips value={mode} onChange={setMode} /></Field>
      {amount !== '' && amount !== r.deposit.required && <div className="warn-box">Different from the required {inr(r.deposit.required)}.</div>}
      <ErrorBox error={sub.error} />
    </Modal>
  );
}

/** Refund an overpayment on an open booking. */
export function RefundOverlay({ id }: { id: string }) {
  const S = useStore(), r = S.rec(id);
  const k = r ? calc(r) : null, owed = k ? Math.max(0, -k.balance) : 0;
  const [amount, setAmount] = useState<number | ''>(owed || '');
  const [mode, setMode] = useState(S.state.settings.payModes[0]!);
  const [ref, setRef] = useState('');
  const [refId] = useState(() => S.newId());
  const sub = useSubmit();
  if (!r || !k) return null;
  const problem = !amount ? 'Enter an amount' : amount > owed ? `Refund is more than the ${inr(owed)} overpaid` : null;
  const save = () => sub.run(async () => { const x = await S.act('record.refund', `record:${id}`, { id, refund: { id: refId, amount, mode, ref: ref.trim() } }, `Refund of ${inr(Number(amount))} recorded`); if (x.ok) S.close(); return x; });
  return (
    <Modal kicker="Refund" title="Refund overpayment" sub={`${S.num(r)} · ${S.cust(r.cust).name}`} onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Cancel</button><button className="btn primary" disabled={!!problem || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Saving…' : 'Record refund'}</button></>}>
      <Figs items={[['Total', inr(k.total)], ['Received', inr(k.paid)], ['Overpaid', inr(owed)]]} />
      <div className="grid2">
        <Field label="Refund amount"><MoneyInput value={amount} onChange={setAmount} /></Field>
        <Field label="Reference"><input className="input" placeholder="Optional" value={ref} onChange={e => setRef(e.target.value)} /></Field>
      </div>
      <Field label="Mode"><ModeChips value={mode} onChange={setMode} /></Field>
      {problem && amount !== '' && <div className="error-box">{problem}</div>}
      <ErrorBox error={sub.error} />
    </Modal>
  );
}

/** Payments are never deleted: a mistaken entry is voided with a reason and stays in the ledger. */
export function VoidOverlay({ id, paymentId }: { id: string; paymentId: string }) {
  const S = useStore(), r = S.rec(id), p = r?.payments.find(x => x.id === paymentId);
  const [reason, setReason] = useState('');
  const sub = useSubmit();
  if (!r || !p) return null;
  const save = () => sub.run(async () => { const x = await S.act('record.voidPayment', `record:${id}`, { id, paymentId, reason: reason.trim() }, 'Payment voided'); if (x.ok) S.close(); return x; });
  return (
    <Modal kicker="Payment" title="Void this payment?" sub={`${PAYTYPE[p.type]} · ${inr(p.amount)} · ${p.mode} · ${fDate(p.date)}`} onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Keep it</button><button className="btn danger" disabled={!reason.trim() || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Saving…' : 'Void payment'}</button></>}>
      <div style={{ fontSize: 14 }}>The payment stays in the ledger, crossed out, with your reason. Use this for an entry made by mistake or recorded twice — not for refunds.</div>
      <Field label="Reason"><input className="input" placeholder="e.g. Recorded twice" value={reason} onChange={e => setReason(e.target.value)} /></Field>
      <ErrorBox error={sub.error} />
    </Modal>
  );
}

export function CancelOverlay({ id }: { id: string }) {
  const S = useStore(), r = S.rec(id);
  const sug = r ? cancelSuggest(r, S.now) : null;
  const [refund, setRefund] = useState<number | ''>(sug?.refund ?? 0);
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState(S.state.settings.payModes[0]!);
  const [notes, setNotes] = useState('');
  const [refId] = useState(() => S.newId());
  const [done, setDone] = useState<{ refund: number; retained: number } | null>(null);
  const sub = useSubmit();
  if (!r || !sug) return null;
  const c = r.terms.cancel;
  const depHeld = r.deposit.collected && r.deposit.status === 'held';
  const rule = sug.rule === 'none' ? 'No cancellation policy on this booking — refund is your call.'
    : sug.rule === 'full' ? `Cancelled ${sug.daysBefore} days before — full refund (${c.fullRefundDays}+ days).`
    : `Cancelled ${sug.daysBefore} day${sug.daysBefore === 1 ? '' : 's'} before — ${c.forfeitPct}% of the ${inr(sug.advance)} advance is retained.`;
  const problem = !reason.trim() ? 'Give a reason' : refund === '' ? 'Enter the refund (0 if none)' : refund > sug.paid ? `Refund cannot be more than the ${inr(sug.paid)} paid` : null;
  const save = () => sub.run(async () => { const x = await S.act('record.cancel', `record:${id}`, { id, cancel: { reason: reason.trim(), refund, mode, refundId: refId, notes } }); if (x.ok) setDone({ refund: Number(refund), retained: sug.paid - Number(refund) }); return x; });
  if (done) return <Modal kicker="Cancellation" title="Booking cancelled" onClose={() => S.close()}><Success title="Dates released" body={`Refund ${inr(done.refund)} · retained ${inr(done.retained)}`} actions={[{ label: 'Print cancellation note', go: () => S.nav('bill', { id }) }, { label: 'WhatsApp cancellation message', go: () => S.open('message', { id, tpl: 'cancel' }) }, { label: 'Done', primary: true, go: () => S.close() }]} /></Modal>;
  return (
    <Modal kicker="Cancellation" title="Cancel booking" sub={`${S.num(r)} · ${S.cust(r.cust).name}`} onClose={() => S.close()}
      footer={<><button className="btn" onClick={() => S.close()}>Keep booking</button><button className="btn danger" disabled={!!problem || sub.busy || !S.canEdit} onClick={save}>{sub.busy ? 'Saving…' : 'Cancel booking'}</button></>}>
      <div className="card tight" style={{ boxShadow: 'none', border: '1px solid var(--line)' }}>
        <div className="kicker-sm">Policy guideline</div>
        <div style={{ fontSize: 14, marginTop: 4 }}>{rule}</div>
        {c.note && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{c.note}</div>}
      </div>
      <Figs items={[['Paid', inr(sug.paid)], ['Refund', inr(Number(refund || 0))], ['Retained', inr(Math.max(0, sug.paid - Number(refund || 0)))]]} />
      <div className="grid2">
        <Field label="Refund amount" hint={`Suggested ${inr(sug.refund)} — your decision`}><MoneyInput value={refund} onChange={setRefund} /></Field>
        <Field label="Reason"><input className="input" placeholder="e.g. Family emergency" value={reason} onChange={e => setReason(e.target.value)} /></Field>
      </div>
      {(Number(refund) > 0 || depHeld) && <Field label="Refund mode"><ModeChips value={mode} onChange={setMode} /></Field>}
      <Field label="Notes"><textarea className="textarea" rows={2} value={notes} onChange={e => setNotes(e.target.value)} /></Field>
      {depHeld && <div className="info-box">The {inr(r.deposit.collected!.amount)} caution deposit already collected is returned in full as well. It is not part of the refund above.</div>}
      {refund !== '' && refund > sug.paid && <div className="error-box">{problem}</div>}
      <ErrorBox error={sub.error} />
    </Modal>
  );
}
